// Driver-side GPS publisher — a module singleton, NOT tied to the delivery
// page. Layout starts it for anyone who can drive; from then on the phone keeps
// sharing its position for every order assigned to this user that is out for
// delivery, no matter which screen of the app is open. Sharing stops on its own
// when the last active delivery is marked delivered/failed.
//
// Each fix goes two ways: an upsert into driver_locations (the "last known
// position" the backend serves to late-joining viewers) and a Realtime
// broadcast on the order's channel (sub-second updates for everyone already
// watching). The broadcast uses Realtime's REST endpoint on purpose: the
// publisher then holds no channel object, so a viewer on the same device (the
// driver opening their own delivery) can subscribe/unsubscribe the same topic
// freely without tearing the publisher down.
import { supabase } from './supabase';
import {
  EV_POSITION, normalizePosition, setGpsStatus, shouldSendPosition, trackingChannel,
} from './deliveryTracking';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Re-read the assigned deliveries this often even without a status event,
// so an assignment made from the office desk reaches the phone on its own.
const REFRESH_MS = 60 * 1000;
// Layout remounts on every route change; the grace period lets the next mount
// cancel the stop so navigation never restarts the GPS watch.
const STOP_GRACE_MS = 1500;
// Window event fired by the delivery screens after a status change.
export const DELIVERY_CHANGED_EVENT = 'zb:delivery-changed';

const S = {
  userId: null, orders: [], watchId: null, last: null,
  refreshTimer: null, stopTimer: null, wakeLock: null, refreshing: false,
  onChanged: null, onVisible: null,
};

// Publish one event on an order's tracking channel through the Realtime REST
// API. Used for positions here and for status changes by the delivery screens.
// Never throws: a lost broadcast is covered by the viewers' polling.
export async function broadcastDelivery(token, event, payload) {
  if (!token || !SUPABASE_URL || !SUPABASE_KEY) return false;
  try {
    let bearer = SUPABASE_KEY;
    try {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.access_token) bearer = data.session.access_token;
    } catch { /* anonymous viewer: the anon key is enough for a public topic */ }
    const res = await fetch(`${SUPABASE_URL}/realtime/v1/api/broadcast`, {
      method: 'POST',
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${bearer}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: [{ topic: trackingChannel(token), event, payload, private: false }] }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// Tell the publisher that a delivery changed state (assigned, out, delivered)
// so it re-reads its queue right away instead of at the next refresh tick.
export function notifyDeliveryChanged() {
  try { window.dispatchEvent(new CustomEvent(DELIVERY_CHANGED_EVENT)); } catch { /* SSR / tests */ }
}

export function startDriverSharing(userId) {
  if (!userId || typeof window === 'undefined') return;
  if (S.stopTimer) { clearTimeout(S.stopTimer); S.stopTimer = null; }
  if (S.userId === userId && S.refreshTimer) return;   // already running for this user
  if (S.userId && S.userId !== userId) stopDriverSharing();
  S.userId = userId;
  S.onChanged = () => { refreshOrders(); };
  S.onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    refreshOrders();
    if (S.watchId != null) requestWakeLock();
  };
  window.addEventListener(DELIVERY_CHANGED_EVENT, S.onChanged);
  document.addEventListener('visibilitychange', S.onVisible);
  S.refreshTimer = setInterval(refreshOrders, REFRESH_MS);
  refreshOrders();
}

// Called from the component's cleanup; the next mount (a route change) cancels it.
export function scheduleStopDriverSharing() {
  if (S.stopTimer) clearTimeout(S.stopTimer);
  S.stopTimer = setTimeout(stopDriverSharing, STOP_GRACE_MS);
}

export function stopDriverSharing() {
  if (S.stopTimer) { clearTimeout(S.stopTimer); S.stopTimer = null; }
  if (S.refreshTimer) { clearInterval(S.refreshTimer); S.refreshTimer = null; }
  if (S.onChanged) { window.removeEventListener(DELIVERY_CHANGED_EVENT, S.onChanged); S.onChanged = null; }
  if (S.onVisible) { document.removeEventListener('visibilitychange', S.onVisible); S.onVisible = null; }
  stopWatch();
  S.userId = null;
  S.orders = [];
  setGpsStatus({ sharing: false, error: null, active: 0, lastSentAt: null });
}

async function refreshOrders() {
  if (!S.userId || S.refreshing) return;
  S.refreshing = true;
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('id, tracking_token, delivery_status')
      .eq('driver_id', S.userId)
      .eq('delivery_status', 'out_for_delivery');
    if (error) throw error;
    const next = data || [];
    const before = S.orders.map((o) => o.id).sort().join(',');
    const after = next.map((o) => o.id).sort().join(',');
    S.orders = next;
    // A newly added delivery should get a marker at once: forget the throttle.
    if (before !== after) S.last = null;
    if (S.orders.length && S.watchId == null) startWatch();
    if (!S.orders.length && S.watchId != null) stopWatch();
    setGpsStatus(S.orders.length ? { active: S.orders.length } : { active: 0, sharing: false, error: null });
  } catch {
    /* transient: keep the previous queue and try again on the next tick */
  } finally {
    S.refreshing = false;
  }
}

function startWatch() {
  if (S.watchId != null) return;
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    setGpsStatus({ sharing: false, error: 'unavailable' });
    return;
  }
  S.last = null;
  S.watchId = navigator.geolocation.watchPosition(onFix, onGpsError, {
    enableHighAccuracy: true, maximumAge: 3000, timeout: 20000,
  });
  requestWakeLock();
}

function stopWatch() {
  if (S.watchId != null && typeof navigator !== 'undefined' && navigator.geolocation) {
    navigator.geolocation.clearWatch(S.watchId);
  }
  S.watchId = null;
  S.last = null;
  releaseWakeLock();
}

async function onFix(pos) {
  const c = pos?.coords;
  if (!c) return;
  const now = Date.now();
  const next = normalizePosition({
    lat: c.latitude, lng: c.longitude, heading: c.heading, accuracy: c.accuracy, speed: c.speed,
    at: new Date(now),
  });
  if (!next) return;
  if (!shouldSendPosition(S.last, next, now)) return;
  S.last = { ...next, sentAt: now };
  const orders = S.orders.slice();
  if (!orders.length) return;
  const payload = { ...next, driver_id: S.userId };
  let anyOk = false;
  await Promise.all(orders.map(async (o) => {
    const row = {
      order_id: o.id, driver_id: S.userId,
      lat: next.lat, lng: next.lng, accuracy: next.accuracy, heading: next.heading,
      updated_at: next.at,
    };
    const [{ error }, sent] = await Promise.all([
      supabase.from('driver_locations').upsert(row, { onConflict: 'order_id' }),
      broadcastDelivery(o.tracking_token, EV_POSITION, payload),
    ]);
    if (error) console.warn('driver_locations upsert failed:', error.message);
    if (!error || sent) anyOk = true;
  }));
  if (anyOk) setGpsStatus({ sharing: true, error: null, lastSentAt: now, active: orders.length });
}

function onGpsError(err) {
  // 1 = permission denied (needs the user), 2 = position unavailable,
  // 3 = timeout. Only a denial is final; the watch keeps trying otherwise.
  if (err?.code === 1) setGpsStatus({ sharing: false, error: 'denied' });
  else if (err?.code === 2) setGpsStatus({ error: 'unavailable' });
  else setGpsStatus({ error: 'timeout' });
}

// Keep the screen on while sharing so the browser doesn't suspend the watch.
async function requestWakeLock() {
  try {
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator) || S.wakeLock) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    S.wakeLock = await navigator.wakeLock.request('screen');
    S.wakeLock.addEventListener('release', () => { S.wakeLock = null; });
  } catch { /* not granted (low battery, unsupported) — sharing still works */ }
}

function releaseWakeLock() {
  try { S.wakeLock?.release(); } catch { /* already released */ }
  S.wakeLock = null;
}
